import { useEffect, useState, useCallback } from "react";
import { api } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { ListChecks, Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const DD_CATEGORIES = {
    financial: "Financial", legal: "Legal", commercial: "Commercial",
    operational: "Operational", tax: "Tax", hr_pensions: "HR & pensions",
};
const STATUSES = [
    { v: "requested", label: "Requested" }, { v: "provided", label: "Provided" },
    { v: "reviewed", label: "Reviewed" }, { v: "flagged", label: "Flagged" },
    { v: "not_applicable", label: "N/A" },
];
const STATUS_CLS = {
    requested: "text-muted-foreground", provided: "text-blue-700", reviewed: "text-emerald-700",
    flagged: "text-red-700", not_applicable: "text-muted-foreground",
};
const NO_DOC = "__none__";

export default function ChecklistPanel({ dealId, onChanged }) {
    const [data, setData] = useState({ items: [], summary: null });
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({ category: "financial", title: "" });

    const load = useCallback(async () => {
        const [c, d] = await Promise.all([api.get(`/dd/deal/${dealId}/checklist`), api.get(`/documents/deal/${dealId}`)]);
        setData(c.data);
        setDocs(d.data.filter((x) => !x.superseded_by));
        setLoading(false);
    }, [dealId]);
    useEffect(() => { load(); }, [load]);

    const fail = (err) => toast.error(err.response?.data?.detail || err.message);
    const seed = async () => {
        try {
            const { data } = await api.post(`/dd/deal/${dealId}/checklist/seed`);
            toast.success(data.added ? `Added ${data.added} standard requests` : "Checklist already up to date");
            await load(); onChanged?.();
        } catch (e) { fail(e); }
    };
    const patch = async (id, body) => {
        try { await api.patch(`/dd/checklist/${id}`, body); await load(); onChanged?.(); } catch (e) { fail(e); }
    };
    const del = async (id) => {
        if (!confirm("Remove this request?")) return;
        try { await api.delete(`/dd/checklist/${id}`); await load(); onChanged?.(); } catch (e) { fail(e); }
    };
    const add = async (e) => {
        e.preventDefault();
        if (!draft.title.trim()) return;
        try {
            await api.post(`/dd/deal/${dealId}/checklist`, { category: draft.category, title: draft.title.trim() });
            setDraft((d) => ({ ...d, title: "" }));
            await load();
        } catch (e2) { fail(e2); }
    };

    const s = data.summary;
    if (loading) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;

    return (
        <div className="space-y-4" data-testid="dd-checklist">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-display font-bold tracking-tight flex items-center gap-2"><ListChecks className="w-4 h-4 text-gold" /> Due diligence requests</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Link each request to the data-room document that answers it. Flagged items are never counted as reviewed.</p>
                </div>
                {s && s.total > 0 ? (
                    <div className="text-right" data-testid="dd-summary">
                        <div className="font-mono text-2xl font-bold text-navy">{s.review_pct ?? 0}%</div>
                        <div className="text-xs text-muted-foreground">{s.reviewed} reviewed · {s.provided}/{s.applicable} provided{s.flagged ? ` · ${s.flagged} flagged` : ""}</div>
                    </div>
                ) : (
                    <Button onClick={seed} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="dd-seed-btn">
                        Add standard request list
                    </Button>
                )}
            </div>
            {s && s.total > 0 && <div className="h-1.5 rounded bg-neutral-100 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${s.review_pct ?? 0}%` }} /></div>}

            {Object.entries(DD_CATEGORIES).map(([cat, label]) => {
                const items = data.items.filter((i) => i.category === cat);
                if (!items.length) return null;
                const b = s?.by_category?.[cat];
                return (
                    <div key={cat} className="border border-border rounded-md bg-white" data-testid={`dd-cat-${cat}`}>
                        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-neutral-50">
                            <span className="text-xs uppercase tracking-wider font-medium">{label}</span>
                            {b && <span className="text-xs text-muted-foreground">{b.provided}/{b.applicable} provided</span>}
                        </div>
                        <table className="w-full text-sm">
                            <tbody>
                                {items.map((i) => (
                                    <tr key={i.id} className="border-b border-border/60 last:border-0" data-testid={`dd-item-${i.id}`}>
                                        <td className="py-2 px-4">
                                            <div className={i.status === "not_applicable" ? "text-muted-foreground line-through" : ""}>{i.title}</div>
                                            {i.open_anomalies > 0 && (
                                                <div className="text-xs text-amber-700 flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3 h-3" /> {i.open_anomalies} open anomal{i.open_anomalies > 1 ? "ies" : "y"} attached</div>
                                            )}
                                        </td>
                                        <td className="py-2 w-56">
                                            <Select value={i.document_id || NO_DOC} onValueChange={(v) => v !== NO_DOC && patch(i.id, { document_id: v })}>
                                                <SelectTrigger className="h-8 text-xs" data-testid={`dd-doc-${i.id}`}><SelectValue placeholder="Link document…" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value={NO_DOC} disabled>{docs.length ? "Link document…" : "No documents uploaded"}</SelectItem>
                                                    {docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.filename}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className="py-2 px-2 w-36">
                                            <Select value={i.status} onValueChange={(v) => patch(i.id, { status: v })}>
                                                <SelectTrigger className={`h-8 ${STATUS_CLS[i.status]}`} data-testid={`dd-status-${i.id}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>{STATUSES.map((x) => <SelectItem key={x.v} value={x.v}>{x.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </td>
                                        <td className="py-2 pr-3 w-10 text-right">
                                            <Button size="icon" variant="ghost" onClick={() => del(i.id)}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}

            {s && s.total > 0 && (
                <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-12 gap-2" data-testid="dd-add-form">
                    <div className="md:col-span-3">
                        <Select value={draft.category} onValueChange={(v) => setDraft((d) => ({ ...d, category: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.entries(DD_CATEGORIES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <Input className="md:col-span-8" placeholder="Add a custom request…" value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} data-testid="dd-custom-input" />
                    <Button type="submit" className="md:col-span-1 bg-foreground text-background hover:bg-neutral-800 hover:text-white"><Plus className="w-4 h-4" /></Button>
                </form>
            )}
        </div>
    );
}
